import { ofType } from 'redux-observable';
import { catchError, concatMap, map, mergeMap } from 'rxjs/operators';
import { asyncScheduler, filter, from, Observable, of, scheduled, withLatestFrom } from 'rxjs';
import { noteReviewSlice } from './';
import { NOTE_REVIEW, client, SUBMIT_NOTE_REVIEW } from '@ax/graphql';
import { transformRemoteStateReview } from './utilities.ts';
import { RootState } from '../../';
import { AnalyticsService } from '@ax/service';
import { selectMixpanelToken } from '../mixpanel/selectors';
import { selectProviderUserInfo } from '../provider/selectors';
import { ANALYTICS_CONSTANTS, AnalyticsProperties, OverallReviewResponse } from '@ax/constants';
import { mixpanelSlice } from '../mixpanel';
import { Action } from '@reduxjs/toolkit';

export const loadNoteReview$ = (action$: Observable<any>) =>
  action$.pipe(
    ofType(noteReviewSlice.actions.loadNoteReviewRequested.type),
    mergeMap((action) =>
      from(
        client.query({
          query: NOTE_REVIEW,
          variables: { noteId: action.payload.noteId },
          fetchPolicy: 'network-only',
        }),
      ).pipe(
        map(({ data: { getNote } }) =>
          noteReviewSlice.actions.loadNoteReviewSucceeded({
            review: transformRemoteStateReview(getNote.review),
          }),
        ),
        catchError((error) =>
          of(
            noteReviewSlice.actions.loadNoteReviewFailed({
              errorMessage: `There has been an error loading the note review. Error: ${
                error?.message ?? ''
              }`,
            }),
          ),
        ),
      ),
    ),
  );

export const submitOverallNoteReview$ = (action$: Observable<any>) =>
  action$.pipe(
    ofType(noteReviewSlice.actions.noteOverallReviewSubmitted.type),
    mergeMap((action) =>
      from(
        client.mutate({
          mutation: SUBMIT_NOTE_REVIEW,
          variables: {
            input: { noteId: action.payload.noteId, overallReview: action.payload.overallReview },
          },
        }),
      ).pipe(
        map(({ data: { addNoteReview } }) =>
          noteReviewSlice.actions.noteOverallReviewSubmissionSucceeded({
            review: transformRemoteStateReview(addNoteReview),
          }),
        ),
        catchError((error) =>
          of(
            noteReviewSlice.actions.noteOverallReviewSubmissionFailed({
              overallReview: action.payload.overallReview,
              noteId: action.payload.noteId,
              errorMessage: `There has been an error submitting the note review. Error: ${
                error?.message ?? ''
              }`,
            }),
          ),
        ),
      ),
    ),
  );

export const submitPoorReviewResponse$ = (action$: Observable<any>) =>
  action$.pipe(
    ofType(noteReviewSlice.actions.poorReviewResponseSubmitted.type),
    mergeMap((action) =>
      from(
        client.mutate({
          mutation: SUBMIT_NOTE_REVIEW,
          variables: {
            input: {
              noteId: action.payload.review.noteId,
              overallReview: action.payload.review.overallReview,
              specificPoorReviews: action.payload.review.poorReviewReasons,
              textResponse: action.payload.review.textResponse,
            },
          },
        }),
      ).pipe(
        map(({ data: { addNoteReview } }) =>
          noteReviewSlice.actions.poorReviewResponseSubmissionSucceeded({
            review: transformRemoteStateReview(addNoteReview),
          }),
        ),
        catchError((error) =>
          of(
            noteReviewSlice.actions.poorReviewResponseSubmissionFailed({
              review: action.payload.review,
              errorMessage: `There has been an error submitting the poor review response. Error: ${
                error?.message ?? ''
              }`,
            }),
          ),
        ),
      ),
    ),
  );

export const dispatchOverallReviewMixpanelEvent$ = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(noteReviewSlice.actions.noteOverallReviewSubmissionSucceeded.type),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      try {
        const mixpanelToken = selectMixpanelToken(state);
        const providerUserInfo = selectProviderUserInfo(state);
        const analyticsService = new AnalyticsService(mixpanelToken, providerUserInfo);

        const { review } = action.payload;

        let dispatchedEvent: { event: string; properties: AnalyticsProperties };

        switch (review.overallReview) {
          case OverallReviewResponse.THUMBS_UP:
            dispatchedEvent = analyticsService.onNoteLevelThumbsUp({
              noteId: review.noteId,
            });
            return [mixpanelSlice.actions.eventDispatchSucceeded(dispatchedEvent)];
          case OverallReviewResponse.THUMBS_DOWN:
            dispatchedEvent = analyticsService.onNoteLevelThumbsDown({
              noteId: review.noteId,
            });
            return [mixpanelSlice.actions.eventDispatchSucceeded(dispatchedEvent)];
          default:
            return [];
        }
      } catch (error: unknown) {
        const { review } = action.payload;
        const event =
          review.overallReview === OverallReviewResponse.THUMBS_UP
            ? ANALYTICS_CONSTANTS.event.noteLevelThumbsUp
            : ANALYTICS_CONSTANTS.event.noteLevelThumbsDown;
        // @ts-ignore
        return [mixpanelSlice.actions.eventDispatchFailed({ event, error: error.message })];
      }
    }),
    catchError((error) => {
      return [mixpanelSlice.actions.eventDispatchFailed({ event: '', error: error.message })];
    }),
  );

export const dispatchPoorReviewResponseMixpanelEvents$ = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(noteReviewSlice.actions.poorReviewResponseSubmissionSucceeded.type),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      try {
        const mixpanelToken = selectMixpanelToken(state);
        const providerUserInfo = selectProviderUserInfo(state);
        const analyticsService = new AnalyticsService(mixpanelToken, providerUserInfo);

        const { review } = action.payload;

        const dispatchedEvents = review.poorReviewReasons.map((reason: string) =>
          analyticsService.onNoteLevelFeedbackReason({
            noteId: review.noteId,
            reason,
          }),
        );

        if (review.textResponse) {
          const textResponseEvent = analyticsService.onNoteLevelFeedbackTextSubmitted({
            noteId: review.noteId,
            textLength: review.textResponse.length.toString(),
            feedbackProvided: true,
          });

          dispatchedEvents.push(textResponseEvent);
        }

        const resultActions: Action[] = dispatchedEvents.map(
          (event: { event: string; properties: AnalyticsProperties }) =>
            mixpanelSlice.actions.eventDispatchSucceeded(event),
        );

        return scheduled(resultActions, asyncScheduler);
      } catch (error: unknown) {
        return [
          // @ts-ignore
          mixpanelSlice.actions.eventDispatchFailed({ event: 'UNKNOWN', error: error.message }),
        ];
      }
    }),
    catchError((error) => {
      return [
        mixpanelSlice.actions.eventDispatchFailed({ event: 'UNKNOWN', error: error.message }),
      ];
    }),
  );

export const dispatchPoorReviewModalOpenedMixpanelEvent$ = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(noteReviewSlice.actions.noteOverallReviewSubmissionSucceeded.type),
    filter((action) => action.payload.review.overallReview === OverallReviewResponse.THUMBS_DOWN),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      try {
        const mixpanelToken = selectMixpanelToken(state);
        const providerUserInfo = selectProviderUserInfo(state);
        const analyticsService = new AnalyticsService(mixpanelToken, providerUserInfo);

        const { review } = action.payload;

        const dispatchedEvent = analyticsService.onNoteLevelFeedbackModalOpened({
          noteId: review.noteId,
        });

        return [mixpanelSlice.actions.eventDispatchSucceeded(dispatchedEvent)];
      } catch (error: unknown) {
        return [
          mixpanelSlice.actions.eventDispatchFailed({
            event: ANALYTICS_CONSTANTS.event.noteLevelFeedbackModalOpened,
            // @ts-ignore
            error: error.message,
          }),
        ];
      }
    }),
    catchError((error) => {
      return [
        mixpanelSlice.actions.eventDispatchFailed({
          event: ANALYTICS_CONSTANTS.event.noteLevelFeedbackModalOpened,
          error: error.message,
        }),
      ];
    }),
  );

export const dispatchPoorReviewModalClosedMixpanelEvent$ = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(noteReviewSlice.actions.poorReviewModalClosed.type),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      try {
        const mixpanelToken = selectMixpanelToken(state);
        const providerUserInfo = selectProviderUserInfo(state);
        const analyticsService = new AnalyticsService(mixpanelToken, providerUserInfo);

        const { noteId } = action.payload;

        const dispatchedEvent = analyticsService.onNoteLevelFeedbackModalClosed({ noteId });

        return [mixpanelSlice.actions.eventDispatchSucceeded(dispatchedEvent)];
      } catch (error: unknown) {
        return [
          mixpanelSlice.actions.eventDispatchFailed({
            event: ANALYTICS_CONSTANTS.event.noteLevelFeedbackModalClosed,
            // @ts-ignore
            error: error.message,
          }),
        ];
      }
    }),
    catchError((error) => {
      return [
        mixpanelSlice.actions.eventDispatchFailed({
          event: ANALYTICS_CONSTANTS.event.noteLevelFeedbackModalClosed,
          error: error.message,
        }),
      ];
    }),
  );

export default [
  loadNoteReview$,
  submitOverallNoteReview$,
  submitPoorReviewResponse$,
  dispatchOverallReviewMixpanelEvent$,
  dispatchPoorReviewResponseMixpanelEvents$,
  dispatchPoorReviewModalOpenedMixpanelEvent$,
  dispatchPoorReviewModalClosedMixpanelEvent$,
];
